import { apiClient } from './api';

export interface PlatformStats {
  totalSalons: number;
  activeSalons: number;
  totalBookings: number;
  totalClients: number;
  totalRevenue: number;
  bookingsByStatus: Record<string, number>;
}

export interface RevenueStats {
  total: number;
  change: number;
  byMonth: { month: string; value: number }[];
}

export interface BookingStats {
  total: number;
  completed: number;
  cancelled: number;
  noShow: number;
  pending: number;
  avgPerDay: number;
}

export interface ClientStats {
  total: number;
  new: number;
  returning: number;
  avgSpend: number;
  topClients: { id: string; firstName: string; lastName: string; visits: number; spent: number }[];
}

export interface ServiceStats {
  id: string;
  name: string;
  count: number;
  revenue: number;
  avgPrice: number;
}

export interface StaffPerformanceStats {
  id: string;
  firstName: string;
  lastName: string;
  bookings: number;
  revenue: number;
  occupancy: number;
}

export interface PaymentBreakdown {
  cash: number;
  card: number;
  blik: number;
  transfer: number;
}

export interface SalonDashboardReport {
  revenue: RevenueStats;
  bookings: BookingStats;
  clients: ClientStats;
  topServices: ServiceStats[];
  staffPerformance: StaffPerformanceStats[];
  paymentBreakdown: PaymentBreakdown;
}

// ==================== EXTENDED ANALYTICS ====================

export interface TimeseriesBucket {
  key: string;
  label: string;
  revenue: number;
  bookings: number;
  clients: number;
}

export interface TimeseriesReport {
  buckets: TimeseriesBucket[];
}

export interface HourlyPoint {
  hour: number;
  revenue: number;
  bookings: number;
}

export interface DayReport {
  date: string;
  dayLabel: string;
  revenue: number;
  bookings: BookingStats;
  hourly: HourlyPoint[];
  paymentBreakdown: PaymentBreakdown;
  staffPerformance: StaffPerformanceStats[];
  topServices: ServiceStats[];
}

export interface WeekDay {
  date: string;
  dayLabel: string;
  dayShort: string;
  revenue: number;
  bookings: number;
}

export interface WeekReport {
  weekStart: string;
  weekEnd: string;
  total: number;
  change: number;
  days: WeekDay[];
  best: WeekDay | null;
  worst: WeekDay | null;
}

export interface PeakCell {
  dayOfWeek: number;
  dayLabel: string;
  hour: number;
  bookings: number;
  revenue: number;
}

export interface PeakHoursReport {
  matrix: PeakCell[];
  topHours: PeakCell[];
}

export interface EmployeeRow {
  id: string;
  firstName: string;
  lastName: string;
  bookings: number;
  revenue: number;
  clientsServed: number;
  occupancy: number;
  scheduledHours: number;
  commissionRate: number;
  estCommission: number;
}

export interface EmployeeServiceStat {
  id: string;
  name: string;
  count: number;
  revenue: number;
}

export interface EmployeeReport {
  id: string;
  firstName: string;
  lastName: string;
  revenue: number;
  bookings: number;
  completed: number;
  cancelled: number;
  noShow: number;
  clientsServed: number;
  occupancy: number;
  scheduledHours: number;
  commissionRate: number;
  estCommission: number;
  services: EmployeeServiceStat[];
  timeseries: { key: string; label: string; revenue: number; bookings: number }[];
}

export const reportApi = {
  async getPlatformStats() {
    const res = await apiClient.get<PlatformStats>('/reports/platform');
    return res.data;
  },

  async getSalonDashboard(salonId: string, dateFrom: string, dateTo: string) {
    const res = await apiClient.get<SalonDashboardReport>(`/reports/salon/${salonId}`, {
      params: { dateFrom, dateTo },
    });
    return res.data;
  },

  async getTimeseries(salonId: string, dateFrom: string, dateTo: string, granularity: 'day' | 'week') {
    const res = await apiClient.get<TimeseriesReport>(`/reports/salon/${salonId}/timeseries`, {
      params: { dateFrom, dateTo, granularity },
    });
    return res.data;
  },

  async getDayStats(salonId: string, date: string) {
    const res = await apiClient.get<DayReport>(`/reports/salon/${salonId}/day`, { params: { date } });
    return res.data;
  },

  async getWeekStats(salonId: string, weekOf: string) {
    const res = await apiClient.get<WeekReport>(`/reports/salon/${salonId}/week`, { params: { weekOf } });
    return res.data;
  },

  async getPeakHours(salonId: string, dateFrom: string, dateTo: string) {
    const res = await apiClient.get<PeakHoursReport>(`/reports/salon/${salonId}/peak-hours`, {
      params: { dateFrom, dateTo },
    });
    return res.data;
  },

  async getEmployees(salonId: string, dateFrom: string, dateTo: string) {
    const res = await apiClient.get<EmployeeRow[]>(`/reports/salon/${salonId}/employees`, {
      params: { dateFrom, dateTo },
    });
    return res.data;
  },

  async getEmployeeStats(salonId: string, staffId: string, dateFrom: string, dateTo: string) {
    const res = await apiClient.get<EmployeeReport>(`/reports/salon/${salonId}/employee/${staffId}`, {
      params: { dateFrom, dateTo },
    });
    return res.data;
  },
};
